import { NgTemplateOutlet } from '@angular/common';
import { Component, computed, input, output, TemplateRef, viewChild } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NumberSuffixPipe } from '@core/pipes/number-suffix.pipe';
import { DatatableComponent } from '@shared/components/datatable/datatable.component';
import {
  EHeaderClasses,
  ICustomColumn,
  IDatatableConfiguration,
  TDatatableColumn,
} from '@shared/components/datatable/datatable.interface';
import { GroupedDatatableComponent } from '@shared/components/grouped-datatable/grouped-datatable.component';
import {
  IGroupedDatatableColumn,
  IGroupedDatatableConfiguration,
  IGroupedDatatableCustomColumn,
} from '@shared/components/grouped-datatable/grouped-datatable.interface';
import { IconBadgeComponent } from '@shared/components/icon-badge/icon-badge.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { IGroupedSkeletonLoaderConfig } from '@shared/components/skeleton-loader/grouped-skeleton-loader.interface';
import { ISkeletonLoaderConfig } from '@shared/components/skeleton-loader/skeleton-loader.interface';
import { SocialBadgeComponent } from '@shared/components/social-badge/social-badge.component';
import { StatusBadgeComponent } from '@shared/components/status-badge/status-badge.component';
import { TextClampTooltipDirective } from '@shared/directives/text-clamp-tooltip.directive';
import { toEventFrontendTab } from '@utilities/event-payload-mapping.util';
import { ECampaignDetailsTab } from '../../campaign-details.interface';
import { ICampaignExportConfig } from '../../campaign-export-config/campaign-export-config.interface';
import {
  ICampaignDetailsKpiGoalsGroupedChildRow,
  ICampaignDetailsKpiGoalsGroupedParentRow,
  ICampaignDetailsKpiGoalsTableRow,
  TCampaignDetailsKpiGoalsGroupedRow,
} from '../campaign-details-kpi-goals.interface';

@Component({
  imports: [
    DatatableComponent,
    GroupedDatatableComponent,
    IconBadgeComponent,
    IconComponent,
    MatTooltipModule,
    SocialBadgeComponent,
    StatusBadgeComponent,
    TextClampTooltipDirective,
    NumberSuffixPipe,
    NgTemplateOutlet,
  ],
  selector: 'app-campaign-details-kpi-goals-table',
  styleUrl: './campaign-details-kpi-goals-table.component.scss',
  templateUrl: './campaign-details-kpi-goals-table.component.html',
})
export class CampaignDetailsKpiGoalsTableComponent {
  protected readonly currentTab = toEventFrontendTab(ECampaignDetailsTab.kpiGoals);

  public readonly campaignExportConfig = input.required<ICampaignExportConfig>();

  public readonly groupedMode = input<boolean>(false);
  public readonly groupedRowData = input<TCampaignDetailsKpiGoalsGroupedRow[]>([]);
  public readonly loading = input<boolean>(false);
  public readonly rowData = input<ICampaignDetailsKpiGoalsTableRow[]>([]);

  public readonly groupedRowGroupClicked = output<ICampaignDetailsKpiGoalsGroupedParentRow>();
  public readonly rowClicked = output<ICampaignDetailsKpiGoalsTableRow>();

  public readonly groupedSkeletonLoaderConfig: IGroupedSkeletonLoaderConfig = {
    childColumns: [
      {
        borderRadius: '4px',
        circleSize: '14px',
        className: 'min-w-0',
        type: 'icon',
      },
      {
        borderRadius: '100vw',
        className: 'min-w-0',
        columnPadding: '8px 0 8px 16px',
        height: '24px',
        textWidth: '90px',
        type: 'text',
      },
      {
        borderRadius: '4px',
        className: 'min-w-0',
        height: '20px',
        textWidth: '120px',
        type: 'text',
      },
      {
        borderRadius: '4px',
        className: 'min-w-0',
        height: '20px',
        textWidth: '80px',
        type: 'text',
      },
    ],
    childIndentPx: 0,
    childRowGridTemplateColumns: '40px 132px 220px minmax(0, 1fr)',
    childRowsPerGroup: 2,
    childWrapperClass: 'box-border min-w-0',
    childWrapperStyle: {
      marginLeft: '18%',
      width: 'calc(82% - 8px)',
    },
    detailMode: 'expanded',
    expandColumn: { className: 'hidden' },
    groupCount: 5,
    headerMargin: '0',
    headerPadding: '0 16px',
    parentColumns: [
      {
        borderRadius: '4px',
        circleSize: '24px',
        className: 'min-w-0',
        hasText: true,
        iconRadius: '100px',
        textWidth: '96px',
        type: 'icon',
      },
      { borderRadius: '4px', className: 'min-w-0', height: '20px', textWidth: '120px', type: 'text' },
      { borderRadius: '4px', className: 'min-w-0', height: '20px', textWidth: '80px', type: 'text' },
      { borderRadius: '4px', className: 'min-w-0', height: '20px', textWidth: '40px', type: 'text' },
    ],
    parentGridTemplateColumns: 'minmax(0,18%) minmax(0,20%) minmax(0,16%) minmax(0,10%) 40px',
    rowSeparatorEnabled: true,
    showColumns: true,
    showExpandAllTogglePlaceholder: false,
    showPaginator: true,
    showSearchField: false,
  };

  public readonly skeletonLoaderConfig: ISkeletonLoaderConfig = {
    columns: [
      {
        borderRadius: '100vw',
        className: 'w-[12.5%]',
        height: '24px',
        rowPadding: '16px 0',
        textWidth: '91px',
        type: 'text',
      },
      { borderRadius: '4px', className: 'w-[15%]', height: '20px', textWidth: '120px', type: 'text' },
      { borderRadius: '4px', className: 'w-[12.5%]', height: '20px', textWidth: '90px', type: 'text' },
      {
        borderRadius: '4px',
        circleSize: '24px',
        className: 'w-[25%]',
        hasText: true,
        iconRadius: '100px',
        textWidth: '96px',
        type: 'icon',
      },
      { borderRadius: '4px', className: 'w-[10%]', height: '20px', textWidth: '60px', type: 'text' },
      { borderRadius: '4px', className: 'w-[10%]', height: '20px', textWidth: '60px', type: 'text' },
      {
        borderRadius: '4px',
        circleSize: '16px',
        className: 'w-[15%]',
        hasText: true,
        textWidth: '120px',
        type: 'icon',
      },
    ],
    headerMargin: '4px 0 0 0',
    rows: 5,
    showColumns: true,
    showHeaderControls: false,
    showSearchField: false,
  };

  public readonly statusColumnTemplate = viewChild.required<TemplateRef<unknown>>('statusColumn');
  public readonly deliverableGroupColumnTemplate = viewChild.required<TemplateRef<unknown>>('deliverableGroupColumn');
  public readonly groupedDeliverableGroupColumnTemplate = viewChild.required<TemplateRef<unknown>>(
    'groupedDeliverableGroupColumn',
  );
  public readonly kpiColumnTemplate = viewChild.required<TemplateRef<unknown>>('kpiColumn');
  public readonly groupedKpiColumnTemplate = viewChild.required<TemplateRef<unknown>>('groupedKpiColumn');
  public readonly groupedKpiValuesColumnTemplate = viewChild.required<TemplateRef<unknown>>('groupedKpiValuesColumn');
  public readonly groupedChildStatusColumnTemplate =
    viewChild.required<TemplateRef<unknown>>('groupedChildStatusColumn');
  public readonly groupedChildIndicatorColumnTemplate =
    viewChild.required<TemplateRef<unknown>>('groupedChildIndicatorColumn');
  public readonly creatorColumnTemplate = viewChild.required<TemplateRef<unknown>>('creatorColumn');
  public readonly currentColumnTemplate = viewChild.required<TemplateRef<unknown>>('currentColumn');
  public readonly targetColumnTemplate = viewChild.required<TemplateRef<unknown>>('targetColumn');
  public readonly kpiCountColumnTemplate = viewChild.required<TemplateRef<unknown>>('kpiCountColumn');
  public readonly dateColumnTemplate = viewChild.required<TemplateRef<unknown>>('dateColumn');

  public readonly columns: TDatatableColumn<ICampaignDetailsKpiGoalsTableRow, string>[] = [
    {
      dataViewStrategy: (row) => row.statusSortValue,
      headerClasses: [EHeaderClasses.minWidth100],
      id: 'status',
      isSortable: true,
    },
    {
      dataViewStrategy: (row) => row.ctaTemplateName,
      headerClasses: [EHeaderClasses.minWidth150],
      id: 'deliverableGroup',
      isSortable: true,
    },
    {
      dataViewStrategy: (row) => row.kpiLabel,
      headerClasses: [EHeaderClasses.minWidth100],
      id: 'kpi',
      isSortable: true,
    },
    {
      dataViewStrategy: (row) => row.creatorName,
      headerClasses: [EHeaderClasses.minWidth150],
      id: 'creator',
      isSortable: true,
    },
    {
      dataViewStrategy: (row) => row.currentValue ?? -Infinity,
      headerClasses: [EHeaderClasses.minWidth100],
      id: 'current',
      isSortable: true,
    },
    {
      dataViewStrategy: (row) => row.targetValue,
      headerClasses: [EHeaderClasses.minWidth100],
      id: 'target',
      isSortable: true,
    },
    {
      dataViewStrategy: (row) => row.dateSortValue,
      headerClasses: [EHeaderClasses.minWidth150],
      id: 'date',
      isSortable: true,
    },
  ];

  public readonly configuration = computed<IDatatableConfiguration<ICampaignDetailsKpiGoalsTableRow>>(() => ({
    defaultSort: { active: 'date', direction: 'desc' },
    isClientSide: true,
    isLoaderEnabled: true,
    isRowClickEnabled: this.campaignExportConfig().allowKpiGoalsRowClick,
    isSearchEnabled: false,
    pageSizeOptions: [5, 10, 25, 50],
    skipInitialRequest: true,
    storageKey: 'campaign-details-kpi-goals-table',
  }));

  public readonly customColumns = computed<ICustomColumn[]>(() => [
    { isSortable: true, name: 'status', template: this.statusColumnTemplate() },
    { isSortable: true, name: 'deliverableGroup', template: this.deliverableGroupColumnTemplate() },
    { isSortable: true, name: 'kpi', template: this.kpiColumnTemplate() },
    { isSortable: true, name: 'creator', template: this.creatorColumnTemplate() },
    { isSortable: true, name: 'current', template: this.currentColumnTemplate() },
    { isSortable: true, name: 'target', template: this.targetColumnTemplate() },
    { isSortable: true, name: 'date', template: this.dateColumnTemplate() },
  ]);

  public readonly groupedParentColumns = computed<IGroupedDatatableColumn<ICampaignDetailsKpiGoalsGroupedParentRow>[]>(
    () => [
      {
        id: 'creator',
        isSearchable: true,
        isSortable: true,
        label: 'field.creator',
        valueGetter: (row): string => row.creatorName,
      },
      {
        id: 'deliverableGroup',
        isSearchable: true,
        isSortable: true,
        label: 'field.deliverableGroup',
        valueGetter: (row): string => row.ctaTemplateName,
      },
      {
        id: 'date',
        isSearchable: true,
        isSortable: true,
        label: 'field.date',
        valueGetter: (row): number => row.dateSortValue,
      },
      {
        id: 'kpiCount',
        isSearchable: false,
        isSortable: true,
        label: 'field.kpis',
        valueGetter: (row): number => row.kpiCount,
      },
    ],
  );

  public readonly groupedChildColumns = computed<IGroupedDatatableColumn<ICampaignDetailsKpiGoalsGroupedChildRow>[]>(
    () => [
      {
        id: 'childIndicator',
        isSearchable: false,
        isSortable: false,
        label: '',
        valueGetter: (): string => '',
      },
      {
        id: 'childStatus',
        isSearchable: false,
        isSortable: false,
        label: 'field.status',
        valueGetter: (row): string => row.kpiGoalStatus,
      },
      {
        id: 'kpi',
        isSearchable: true,
        isSortable: false,
        label: 'field.kpi',
        valueGetter: (row): string => row.sourceRow.kpiLabel,
      },
      {
        id: 'kpiValues',
        isSearchable: false,
        isSortable: false,
        label: '',
        valueGetter: (): string => '',
      },
    ],
  );

  public readonly groupedParentCustomColumns = computed<
    IGroupedDatatableCustomColumn<ICampaignDetailsKpiGoalsGroupedParentRow>[]
  >(() => [
    { id: 'creator', template: this.creatorColumnTemplate() },
    { id: 'date', template: this.dateColumnTemplate() },
    { id: 'deliverableGroup', template: this.groupedDeliverableGroupColumnTemplate() },
    { id: 'kpiCount', template: this.kpiCountColumnTemplate() },
  ]);

  public readonly groupedChildCustomColumns = computed<
    IGroupedDatatableCustomColumn<ICampaignDetailsKpiGoalsGroupedChildRow>[]
  >(() => [
    { id: 'childIndicator', template: this.groupedChildIndicatorColumnTemplate() },
    { id: 'childStatus', template: this.groupedChildStatusColumnTemplate() },
    { id: 'kpi', template: this.groupedKpiColumnTemplate() },
    { id: 'kpiValues', template: this.groupedKpiValuesColumnTemplate() },
  ]);

  public readonly groupedConfiguration = computed<
    IGroupedDatatableConfiguration<ICampaignDetailsKpiGoalsGroupedParentRow, ICampaignDetailsKpiGoalsGroupedChildRow>
  >(() => ({
    childIndentPx: 0,
    defaultSort: { active: 'date', direction: 'desc' },
    hideParentsWithoutVisibleChildren: true,
    isChildHeaderEnabled: false,
    isChildRowClickEnabled: this.campaignExportConfig().allowKpiGoalsRowClick,
    isPaginationEnabled: true,
    isParentRowClickEnabled: this.campaignExportConfig().allowDeliverableGroupClick,
    isParentRowToggleEnabled: true,
    isSearchEnabled: false,
    pageSizeOptions: [5, 10, 25, 50],
    parentSortAccessor: (parent, sortKey): Date | number | string | null | undefined => {
      if (sortKey === 'creator') {
        return parent.creatorName;
      }

      if (sortKey === 'deliverableGroup') {
        return parent.ctaTemplateName;
      }

      if (sortKey === 'kpiCount') {
        return parent.kpiCount;
      }

      if (sortKey === 'date') {
        return parent.dateSortValue;
      }

      return '';
    },
    preserveExpandStateOnFilter: true,
    rowSeparatorEnabled: true,
    showExpandAllToggle: true,
    storageKey: 'campaign-details-kpi-goals-grouped-table',
  }));

  public onGroupedChildRowClicked(row: ICampaignDetailsKpiGoalsGroupedChildRow): void {
    if (!this.campaignExportConfig().allowKpiGoalsRowClick) {
      return;
    }

    this.rowClicked.emit(row.sourceRow);
  }

  public onGroupedParentRowClicked(event: Event, row: ICampaignDetailsKpiGoalsGroupedParentRow): void {
    event.stopPropagation();

    if (!this.groupedMode() || !this.campaignExportConfig().allowDeliverableGroupClick) {
      return;
    }

    this.groupedRowGroupClicked.emit(row);
  }
}
